// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { Flex } from '@iheartjane/reefer';
import { useEffect } from 'react';
import { useSwipeable } from 'react-swipeable';

import { CarouselBannerPagination } from '../carouselBannerPagination/carouselBannerPagination';
import { CarouselItem } from '../carouselItem/carouselItem';
import type { CarouselBanner } from '../types/carousel';

interface CarouselSwiperProps {
  activeIndex: number;
  items: CarouselBanner[];
  storeName?: string;
  updateIndex: (newIndex: number) => void;
  variant?: 'brand' | 'product';
}

const CarouselWrapper = styled.div({
  position: 'relative',
  overflow: 'hidden',
  aspectRatio: 343 / 240,
  maxHeight: '240px',
  width: '100%',
  backfaceVisibility: 'hidden',
  transform: 'translate3d(0,0,0)',
  cursor: 'pointer',
  borderRadius: '12px',
  '@media (min-width: 768px)': {
    aspectRatio: 1312 / 320,
    maxHeight: 'unset',
  },
});

const CarouselTrack = styled.div({
  position: 'relative',
  whiteSpace: 'nowrap',
  transition: 'transform 0.3s',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
});

export const CarouselSwiper = ({
  activeIndex,
  items,
  storeName,
  updateIndex,
}: CarouselSwiperProps) => {
  const totalItems = items.length;

  const nextIndex = (activeIndex + 1) % totalItems;
  const prevIndex = (activeIndex + totalItems - 1) % totalItems;

  const touchHandlers = useSwipeable({
    onSwipedLeft: () => {
      updateIndex(nextIndex);
    },
    onSwipedRight: () => {
      updateIndex(prevIndex);
    },
  });

  const arrowKeyHandlers = (event: KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      updateIndex(prevIndex);
    }

    if (event.key === 'ArrowRight') {
      updateIndex(nextIndex);
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', arrowKeyHandlers);

    return () => {
      window.removeEventListener('keyup', arrowKeyHandlers);
    };
  }, [activeIndex]);

  const onCarouselItemClick = (index: number) => () => {
    updateIndex(index);
  };

  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      <Flex justifyContent="center" alignItems="center" width="100%">
        <CarouselWrapper>
          <CarouselTrack
            {...touchHandlers}
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {items.map((item, index) => (
              <CarouselItem
                key={index}
                activeIndex={activeIndex}
                imageSrc={item.image_url}
                imageAlt={item.alt_text ?? `${storeName} Marketing Banner`}
                onClick={onCarouselItemClick(index)}
                variant="product"
                url={item.url}
                index={index}
              />
            ))}
          </CarouselTrack>
          {totalItems > 1 && (
            <CarouselBannerPagination
              activeIndex={activeIndex}
              totalItems={totalItems}
              updateIndex={updateIndex}
            />
          )}
        </CarouselWrapper>
      </Flex>
    </Flex>
  );
};
